import React, { useState, useMemo, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Form, Badge } from 'react-bootstrap';

// Expensive calculation function
const expensiveCalculation = (num) => {
  console.log('🔢 Running expensive calculation...');
  let result = 0;
  for (let i = 0; i < 1000000000; i++) {
    result += num;
  }
  return result;
};

const Lesson14_UseMemo = () => {
  const [count, setCount] = useState(0);
  const [number, setNumber] = useState(5);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    console.log('📖 Lesson 14: useMemo Hook - Loaded');
  }, []);

  // WITHOUT useMemo - recalculates on every render
  const withoutMemoStart = performance.now();
  const resultWithoutMemo = expensiveCalculation(number);
  const withoutMemoTime = performance.now() - withoutMemoStart;

  // WITH useMemo - only recalculates when 'number' changes
  const resultWithMemo = useMemo(() => {
    console.log('🚀 useMemo: Calculating (only when number changes)');
    return expensiveCalculation(number);
  }, [number]);

  console.log('⏱️ Without useMemo:', withoutMemoTime.toFixed(2), 'ms');
  console.log('✅ With useMemo: Only calculates when number changes');

  return (
    <LessonLayout 
      lessonNumber={14}
      title="useMemo Hook" 
      description="Optimize performance with memoization"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>useMemo for expensive calculations</li>
          <li>Memoization concept</li>
          <li>Dependency array with useMemo</li>
          <li>When to use useMemo</li>
        </ul>
      </Alert>

      <Alert variant="danger" className="mt-3">
        <strong>⚠️ Note:</strong> This example may slow down your browser briefly to demonstrate the difference!
      </Alert>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="bg-danger text-white">
              Without useMemo <Badge bg="warning">Slow</Badge>
            </Card.Header>
            <Card.Body>
              <h5>Result: {resultWithoutMemo}</h5>
              <Alert variant="warning" className="mb-0">
                ⚠️ This calculation runs on EVERY render, even when unrelated state (count or theme) changes!
              </Alert>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="bg-success text-white">
              With useMemo <Badge bg="success">Optimized</Badge>
            </Card.Header>
            <Card.Body>
              <h5>Result: {resultWithMemo}</h5>
              <Alert variant="success" className="mb-0">
                ✅ This calculation ONLY runs when 'number' changes. Changing count or theme won't recalculate!
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header>Interactive Controls</Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Change Number (triggers recalculation)</Form.Label>
                <Form.Control
                  type="number"
                  value={number}
                  onChange={(e) => setNumber(parseInt(e.target.value) || 0)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Label>Counter: {count}</Form.Label>
              <div>
                <Button onClick={() => setCount(count + 1)} variant="primary">
                  Increment (no recalc)
                </Button>
              </div>
            </Col>

            <Col md={4}>
              <Form.Label>Theme: {theme}</Form.Label>
              <div>
                <Button 
                  onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')} 
                  variant="secondary"
                >
                  Toggle Theme (no recalc)
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>useMemo Syntax</Card.Header>
        <Card.Body>
          <pre className="code-example">
{`// useMemo takes a function and dependency array
const memoizedValue = useMemo(() => {
  // Expensive calculation
  return computeExpensiveValue(a, b);
}, [a, b]); // Only recalculates when a or b changes

// Without useMemo
const value = computeExpensiveValue(a, b); 
// ↑ Runs on EVERY render

// With useMemo
const value = useMemo(
  () => computeExpensiveValue(a, b), 
  [a, b]
);
// ↑ Only runs when a or b changes`}
          </pre>
        </Card.Body>
      </Card>

      <h4 className="mt-4">📊 When to Use useMemo</h4>
      <Card>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Alert variant="success">
                <strong>✅ Good Use Cases:</strong>
                <ul className="mb-0">
                  <li>Expensive calculations</li>
                  <li>Filtering/sorting large arrays</li>
                  <li>Complex object transformations</li>
                  <li>Preventing object/array recreation</li>
                </ul>
              </Alert>
            </Col>
            <Col md={6}>
              <Alert variant="danger">
                <strong>❌ Don't Use For:</strong>
                <ul className="mb-0">
                  <li>Simple calculations</li>
                  <li>Premature optimization</li>
                  <li>Every single value</li>
                  <li>When dependencies change often</li>
                </ul>
              </Alert>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Profile first - don't optimize prematurely</li>
          <li>Use for expensive calculations with stable dependencies</li>
          <li>Remember: useMemo itself has overhead</li>
          <li>Similar to useCallback but for values instead of functions</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson14_UseMemo;
